import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import UploadIcon from '@mui/icons-material/Upload';
import { LoadingButton } from '@mui/lab';
import { TextField } from '@mui/material';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { mediaService } from '../../api';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { fundManagerFormFields } from '../../forms/fields/formFields';
import { fundManagerFormSchema } from '../../forms/validationSchema/formSchema';
import { useAppDispatch } from '../../redux/hooks';
import { createFundManager, updateFundManager } from '../../redux/modules/fundManager/fundManager.actions';
import { fundManagerSelector } from '../../redux/modules/fundManager/fundManager.selectors';
import { FundManager } from '../../types/fundManager';

interface FundManagerDialogProps {
  open: boolean;
  initialValue: FundManager | null;
  onClose: (isReload: boolean) => void;
}

export function FundManagerDialog({ open, initialValue, onClose }: FundManagerDialogProps) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { handleSubmit, values, handleChange, touched, errors, submitForm, setValues, setFieldValue, resetForm } =
    useFormik({
      initialValues: {
        [fundManagerFormFields.nameEn]: '',
        [fundManagerFormFields.nameAr]: '',
        [fundManagerFormFields.descriptionEn]: '',
        [fundManagerFormFields.descriptionAr]: '',
        [fundManagerFormFields.locationEn]: '',
        [fundManagerFormFields.locationAr]: '',
        [fundManagerFormFields.logoUrl]: '',
      },
      validationSchema: fundManagerFormSchema,
      onSubmit: (formValues) => {
        if (initialValue?.id) {
          dispatch(updateFundManager({ id: initialValue.id, ...formValues }));
        } else {
          dispatch(createFundManager(formValues));
        }
      },
    });
  const { createStatus, updateStatus, error } = useSelector(fundManagerSelector);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoUploading, setLogoUploading] = useState(false);

  const logoUrl = values[fundManagerFormFields.logoUrl] ?? '';

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    setFieldValue(fundManagerFormFields.logoUrl, '');
    try {
      const response = await mediaService.uploadAnonymousPublic(file, 'fundManager');
      if (response?.data?.url) {
        setFieldValue(fundManagerFormFields.logoUrl, response.data.url);
      }
    } catch (err) {
      console.error('Logo upload error:', err);
    } finally {
      setLogoUploading(false);
    }
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleClearLogo = () => {
    setFieldValue(fundManagerFormFields.logoUrl, '');
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const logoSize = 80;

  useEffect(() => {
    if (!open) {
      resetForm();
      setLogoUploading(false);
    }
  }, [open]);

  useEffect(() => {
    const url = initialValue?.logoUrl ?? '';
    setValues({
      nameEn: initialValue?.nameEn || '',
      nameAr: initialValue?.nameAr || '',
      descriptionEn: initialValue?.descriptionEn || '',
      descriptionAr: initialValue?.descriptionAr || '',
      locationEn: initialValue?.locationEn || '',
      locationAr: initialValue?.locationAr || '',
      [fundManagerFormFields.logoUrl]: url,
    });
  }, [initialValue]);

  useEffect(() => {
    if (createStatus === fetchStatuses.success) {
      onClose(true);
      enqueueSnackbar('Fund Manager created!', { variant: 'success' });
    }
    if (createStatus === fetchStatuses.rejected) {
      enqueueSnackbar('Fund Manager creation error!', { variant: 'error' });
    }

    if (updateStatus === fetchStatuses.success) {
      onClose(true);
      enqueueSnackbar('Fund Manager updated!', { variant: 'success' });
    }
  }, [createStatus, updateStatus]);

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>{initialValue?.id ? 'Edit fundManager' : 'Create a new FundManager'}</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Box sx={{ mt: 1, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              style={{ display: 'none' }}
              id="fund-manager-logo"
            />
            <label htmlFor="fund-manager-logo" style={{ cursor: logoUploading ? 'wait' : 'pointer' }}>
              <Box
                component="span"
                sx={{
                  position: 'relative',
                  width: logoSize,
                  height: logoSize,
                  borderRadius: '50%',
                  border: '2px dashed',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  bgcolor: logoUrl ? 'transparent' : 'action.hover',
                  pointerEvents: logoUploading ? 'none' : 'auto',
                  '&:hover': logoUploading ? {} : { borderColor: 'primary.main', bgcolor: 'action.selected' },
                }}
              >
                {logoUploading && <CircularProgress size={36} />}
                {logoUrl && !logoUploading && (
                  <Avatar src={logoUrl} alt="Logo" sx={{ width: logoSize, height: logoSize }} />
                )}
                {!logoUrl && !logoUploading && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <UploadIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      Select logo
                    </Typography>
                  </Box>
                )}
              </Box>
            </label>
            {logoUploading && (
              <Typography variant="body2" color="text.secondary">
                Uploading…
              </Typography>
            )}
            {logoUrl && !logoUploading && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Logo uploaded
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={handleClearLogo}
                >
                  Clear
                </Button>
              </>
            )}
          </Box>
          <DialogContentText>
            {error && <Alert severity="error">{error.message}</Alert>}
            Enter the Fund Manager name
          </DialogContentText>
          <TextField
            margin="normal"
            required
            fullWidth
            id="nameEn"
            name={fundManagerFormFields.nameEn}
            label="Name En"
            value={values[fundManagerFormFields.nameEn]}
            onChange={handleChange}
            error={touched.nameEn && !!errors.nameEn}
            helperText={touched.nameEn && errors.nameEn}
          />
          <TextField
            dir="rtl"
            margin="normal"
            required
            fullWidth
            id="nameAr"
            name={fundManagerFormFields.nameAr}
            label="Name Ar"
            value={values[fundManagerFormFields.nameAr]}
            onChange={handleChange}
            error={touched.nameAr && !!errors.nameAr}
            helperText={touched.nameAr && errors.nameAr}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            multiline
            id={fundManagerFormFields.descriptionEn}
            name={fundManagerFormFields.descriptionEn}
            label="Description En"
            value={values[fundManagerFormFields.descriptionEn]}
            onChange={handleChange}
            error={touched.descriptionEn && !!errors.descriptionEn}
            helperText={touched.descriptionEn && errors.descriptionEn}
          />
          <TextField
            dir="rtl"
            margin="normal"
            required
            fullWidth
            multiline
            id={fundManagerFormFields.descriptionAr}
            name={fundManagerFormFields.descriptionAr}
            label="Description Ar"
            value={values[fundManagerFormFields.descriptionAr]}
            onChange={handleChange}
            error={touched.descriptionAr && !!errors.descriptionAr}
            helperText={touched.descriptionAr && errors.descriptionAr}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            id={fundManagerFormFields.locationEn}
            name={fundManagerFormFields.locationEn}
            label="Location En"
            value={values[fundManagerFormFields.locationEn]}
            onChange={handleChange}
            error={touched.locationEn && !!errors.locationEn}
            helperText={touched.locationEn && errors.locationEn}
          />
          <TextField
            dir="rtl"
            margin="normal"
            required
            fullWidth
            id={fundManagerFormFields.locationAr}
            name={fundManagerFormFields.locationAr}
            label="Location Ar"
            value={values[fundManagerFormFields.locationAr]}
            onChange={handleChange}
            error={touched.locationAr && !!errors.locationAr}
            helperText={touched.locationAr && errors.locationAr}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          type="button"
          color="primary"
          variant="contained"
          endIcon={<SaveIcon />}
          loading={createStatus === fetchStatuses.pending}
          loadingPosition="end"
          onClick={submitForm}
        >
          {initialValue?.id ? 'Save' : 'Create'}
        </LoadingButton>
        <Button color="inherit" variant="outlined" onClick={() => onClose(false)}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
